<template>
  <div>
    <div class="flex flex-col">
      <label class="font-semibold text-sm text-grey-700 mb-2">
        {{ $t('frontend_groovemail_adminsetting_category') }}
      </label>
      <text-input
        class="inline-block mr-2"
        variant="base"
        v-model="folderName"
        :validator="$v.folderName"
        :placeholder="$t('frontend_groovemail_admin_global_templates_enter_category_header')"
      />
    </div>
    <div class="flex justify-end mt-6">
      <gd-button
        size="auto"
        @click="closeFolderCreateModal"
        >{{ $t('frontend_cancel') }}</gd-button
      >
      <gd-button
        variant="primary"
        size="auto"
        class="ml-2"
        @click="
          catId ? updateFolderForTemplate() : saveFolderForTemplate()
        "
      >
        {{
          catId
          ? $t('frontend_update')
          : category
            ? $t('frontend_clone')
            : $t('frontend_save')
        }}</gd-button
      >
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { required, maxLength, minLength } from 'vuelidate/lib/validators';
import showAlert from '@/components/sweet-alert-popup.js';
import themeConfig from '@/theme/themeConfig';

export default {
  middleware: 'auth',
  name: 'folder-create',
  props: {
    from: {
      required: true,
    },
    catId: {
      required: false,
      default: null,
    },
    category: {
      required: false,
      default: '',
    },
  },

  data() {
    return {
      isFolderCreateSubmitted: false,
      folderName: '',
    };
  },

  validations: {
    folderName: {
      required,
      minLength: minLength(3),
      maxLength: maxLength(50),
    },
  },

  created() {
    this.folderName = this.category;
  },

  methods: {
    // error message for the request field
    async requestErrorAlert(error) {
      // hide the loader
      this.hideLoader();
      
      await showAlert({
        iconType: 'alert-error',
        html : `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark mb-3">${error || this.$t('frontend_backend_error') }</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },

    /**
     * Saves the current folder for the template and emits an event to notify the parent component.
     */
    async saveFolderForTemplate() {

      // Mark the folder creation form as submitted and validate the folder name.
      this.isFolderCreateSubmitted = true;
      this.$v.folderName.$touch();

      // Initialize the folder response variable.
      let folderRes = '';

      /**
       * Creates a new folder with the specified name and path.
       * @param {string} path - The path to create the folder at.
      */
      const createFolder = async (path) => {
        try {
          // Show the loader while the request is being made.
          this.$emit('showLoader');

          // Send a POST request to create the folder.
          const folderPayload = {
            name: this.folderName,
          };
          folderRes = await axios.post(path, folderPayload);

          // Hide the loader after the request is complete.
          this.$emit('hideLoader');

          // If the folder already exists, show an error message.
          if (folderRes.data.status === 403) {
            showAlert({
              iconType: 'alert-error',
              html: `
                <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
                <p class="text-sm text-deep-dark mb-3"> ${this.$t('frontend_groovemail_admin_global_templates_category_already_exist')}</p>
              `,
              showCancelButton: false,
              showCloseButton: true,
              confirmButtonColor: themeConfig.appPrimaryColor,
              confirmButtonText: this.$t('frontend_close'),
            });
            return;
          }

          // Emit an event to notify the parent component that the folder has been created.
          this.$emit('folderCreatedForTemplates', true, folderRes.data.data.id);
          this.$v.folderName.$reset();
          this.folderName = '';
        } catch (e) {
          // Show error alert
          this.requestErrorAlert(e.response.data.error);
        }
      };

      // If the folder is being created for the global templates, create the folder at the admin template subcategory path.
      if (this.from === 'global' && !this.$v.folderName.$invalid) {
        await createFolder('/admin/template-subcategory');

      } else if (!this.$v.folderName.$invalid) {
        // If the folder is being created for the user templates, create the folder at the user template folders path.
        await createFolder('/user-template-folders'); 
      }
    },

    async updateFolderForTemplate() {
      this.isFolderCreateSubmitted = true;
      this.$v.folderName.$touch();
      let folderRes = '';
      if (!this.$v.folderName.$invalid) {
        try {
          this.$emit('showLoader');
          const folderPayload = {
            name: this.folderName,
          };
          folderRes = await axios.put(
            `/template-subcategory/${this.catId}`,
            folderPayload
          );
          this.$emit('hideLoader');
          if (folderRes.data.status === 403) {
            showAlert({
              iconType: 'alert-error',
              html: `
                <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
                <p class="text-sm text-deep-dark mb-3"> ${this.$t('frontend_groovemail_admin_global_templates_category_already_exist')}</p>
              `,
              showCancelButton: false,
              showCloseButton: true,
              confirmButtonColor: themeConfig.appPrimaryColor,
              confirmButtonText: this.$t('frontend_close'),
            });
            return;
          }
          
          this.$emit('folderUpdatedForTemplates', true);
          this.$v.folderName.$reset();
          this.folderName = '';

        } catch (e) {
          // show error alert
          this.requestErrorAlert(e.response.data.error);
        }
      }
    },

    closeFolderCreateModal() {
      this.$emit('closeFolderCreateModal');
    },
  },
};
</script>
